import { useEffect, useRef } from "react";
import { useLanguage } from "@/i18n/LanguageProvider";
import {
  METAPERSON_MOBILE_URL,
  METAPERSON_ORIGIN,
  savePendingAvatar,
  type MetaPersonExportResult,
  type MetaPersonGender,
} from "@/lib/metaperson/metapersonCreator";

interface MetaPersonCreatorProps {
  accessToken: string;
  gender: MetaPersonGender;
  avatarCodeToEdit?: string;
  onExported: (result: MetaPersonExportResult) => void;
  onError: (reason: string) => void;
  onCancel: () => void;
}

// Time we wait for the Creator's 'metaperson_creator_loaded' handshake before
// giving up (slow network / blocked iframe).
const LOADED_TIMEOUT_MS = 20000;

export default function MetaPersonCreator({
  accessToken,
  gender,
  avatarCodeToEdit,
  onExported,
  onError,
  onCancel,
}: MetaPersonCreatorProps) {
  const { t } = useLanguage();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Survives re-renders: the handshake fires once, so a stale `let loaded`
  // captured in a re-armed effect would fire a spurious timeout.
  const loadedRef = useRef(false);
  // Config must run exactly once even if the Creator emits BOTH loaded
  // event variants (see the switch below).
  const configuredRef = useRef(false);
  // Latest callbacks / translator kept in refs so the main effect can depend
  // only on the iframe config (and not re-arm the 20s timeout every time the
  // parent passes a new non-memoized onExported/onError).
  const onExportedRef = useRef(onExported);
  const onErrorRef = useRef(onError);
  const tRef = useRef(t);
  useEffect(() => {
    onExportedRef.current = onExported;
    onErrorRef.current = onError;
    tRef.current = t;
  });

  useEffect(() => {
    loadedRef.current = false;
    const loadedTimer = window.setTimeout(() => {
      if (!loadedRef.current) onErrorRef.current("creator_load_timeout");
    }, LOADED_TIMEOUT_MS);

    const post = (msg: Record<string, unknown>) => {
      iframeRef.current?.contentWindow?.postMessage(msg, METAPERSON_ORIGIN);
    };

    const handler = (evt: MessageEvent) => {
      // Origin + source guard — without BOTH, any window could forge a
      // model_exported and inject an arbitrary avatar URL.
      if (evt.origin !== METAPERSON_ORIGIN) return;
      const data = evt.data;
      if (!data || data.source !== "metaperson_creator") return;

      switch (data.eventName) {
        // The WEB JS API documents 'metaperson_creator_loaded'; the official
        // MOBILE samples (avatarsdk/metaperson-android-sample and
        // metaperson-loader-unity MobileIntegrationViaVuplex) wait for
        // 'mobile_loaded' from this same mobile generator URL. Handle BOTH:
        // if only 'mobile_loaded' fires, the old single-name handshake never
        // applied set_export_parameters — which is why the export format
        // selector (GLB/PNG) was still shown to users (12/06).
        case "metaperson_creator_loaded":
        case "mobile_loaded": {
          loadedRef.current = true;
          window.clearTimeout(loadedTimer);
          if (configuredRef.current) break; // both variants fired — configure once
          configuredRef.current = true;
          // Order matters (matches the official samples): authenticate first
          // (token only — never client secret), then export + UI params, then
          // optionally open an avatar. With export parameters preset
          // (format=glb, lod=1, textureProfile=1K.png — exactly what
          // metaperson-import expects), the Creator has no format decision
          // left to ask the user.
          post({ eventName: "authenticate", accessToken });
          post({
            eventName: "set_export_parameters",
            format: "glb",
            lod: 1,
            textureProfile: "1K.png",
            useZip: false,
          });
          post({
            eventName: "set_ui_parameters",
            theme: "dark",
            gender,
            age: "adult",
            isLoginButtonVisible: false,
            isHomeButtonVisible: false,
            isNoPhotoVisible: false,
            isExportButtonVisible: true,
            exportButtonText: tRef.current("metaperson.creator.exportButton"),
            // Close the export modal as soon as the export completes — the
            // app takes over (importing phase) the moment model_exported
            // arrives; leaving the Creator's link dialog up double-prompts.
            closeExportDialogWhenExportCompleted: true,
            skipViewerControls: ["age", "animations", "lighting"],
          });
          if (avatarCodeToEdit) {
            post({ eventName: "show_avatar", avatarCode: avatarCodeToEdit });
          }
          break;
        }
        case "model_generated": {
          // Fires AFTER generation but BEFORE export (official MetaPerson
          // event, carries avatarCode + gender). Persist the avatarCode now so
          // a reload / backgrounded tab between here and model_exported can
          // resume the SAME avatar via show_avatar — no second selfie.
          // Best-effort: savePendingAvatar swallows storage errors, never
          // throws into the message handler.
          if (typeof data.avatarCode === "string" && data.avatarCode) {
            savePendingAvatar(
              data.avatarCode,
              typeof data.gender === "string" ? data.gender : "",
            );
          }
          break;
        }
        case "model_exported": {
          // data.url may be short-lived — hand off to import immediately.
          onExportedRef.current({
            url: typeof data.url === "string" ? data.url : "",
            avatarCode: typeof data.avatarCode === "string" ? data.avatarCode : "",
            gender: typeof data.gender === "string" ? data.gender : "",
            avatarState: data.avatarState,
          });
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("message", handler);
    return () => {
      window.clearTimeout(loadedTimer);
      window.removeEventListener("message", handler);
    };
  }, [accessToken, gender, avatarCodeToEdit]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "hsl(222 38% 6%)" }}>
      <div className="flex items-center justify-end px-4 pt-12 pb-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] font-display text-foreground/60 hover:text-foreground transition-colors"
        >
          {t("common.cancel")}
        </button>
      </div>
      <iframe
        ref={iframeRef}
        title="MetaPerson Creator"
        src={METAPERSON_MOBILE_URL}
        allow="camera; microphone; fullscreen"
        className="flex-1 w-full border-0"
        style={{ background: "hsl(222 38% 6%)" }}
      />
    </div>
  );
}
